import { Input, ItandiChip, RiCalendarLine } from "@itandi/itandi-bb-ui";
import React from "react";
import { useFormContext } from "react-hook-form";
import { Datepicker } from "~/components/common/parts/datepicker/Datepicker";
import type { Deal } from "~/models/Deal";
import type { DealParam, RelatedDealParam } from "~/models/DealForm";
import type { Leasing } from "~/models/Leasing";
import { toDate } from "~/utils/DateUtil";
import { onChangeDate } from "~/utils/FormUtil";
import Styles from "./Block.module.scss";

type Props = {
	deal?: Deal;
	dealType: string | null;
	leasing: Leasing | null;
};
export const MoveOutBlock: React.FC<Props> = ({
	deal,
	dealType = null,
	leasing = null
}: Props) => {
	const { register, setValue, watch } = useFormContext<
		DealParam | RelatedDealParam
	>();
	const terminationDate = watch("terminationDate");
	const inspectionCandidateDate1 = watch("inspectionCandidateDate1");
	const inspectionCandidateDate2 = watch("inspectionCandidateDate2");
	const inspectionCandidateDate3 = watch("inspectionCandidateDate3");
	const isRestorationDealType = watch("dealType") === "restoration";
	React.useEffect(() => {
		if (deal) {
			setValue("terminationDate", deal.terminationDate ?? null);
		} else if (leasing != null && dealType === "restoration") {
			// leasing の日時情報は退去関連の情報
			setValue("terminationDate", leasing.terminationDate);
			setValue("inspectionCandidateDate1", leasing.inspectionCandidateDate1);
			setValue("inspectionCandidateDate2", leasing.inspectionCandidateDate2);
			setValue("inspectionCandidateDate3", leasing.inspectionCandidateDate3);
			setValue(
				"inspectionCandidateDateTime1",
				leasing.inspectionCandidateTimeRange1
			);
			setValue(
				"inspectionCandidateDateTime2",
				leasing.inspectionCandidateTimeRange2
			);
			setValue(
				"inspectionCandidateDateTime3",
				leasing.inspectionCandidateTimeRange3
			);
		}
	}, [deal, dealType, leasing, setValue]);
	return (
		<div className={Styles.Block}>
			<div className={Styles.BlockCaptionWrapper}>
				<div className={Styles.BlockCaption}>
					<span className={Styles.BlockIconWrapper}>
						<RiCalendarLine className={Styles.BlockIcon} />
					</span>
					<span className={Styles.BlockCaptionLabel}>
						{isRestorationDealType ? "退去情報" : "立会日程情報"}
					</span>
				</div>
			</div>
			<div className={Styles.BlockForms}>
				{isRestorationDealType ? (
					<div className={Styles.FormRow}>
						<div className={Styles.FormLabel}>
							<span className={Styles.FormLabelText}>解約日</span>
							<span className={Styles.RequiredLabel}>
								<ItandiChip label="任意" />
							</span>
						</div>
						<div className={Styles.FormContent}>
							<Datepicker
								onChange={(date): void => {
									onChangeDate(date, "terminationDate", setValue);
								}}
								selected={toDate(terminationDate)}
							/>
						</div>
					</div>
				) : null}
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>
							{`${isRestorationDealType ? "退去" : ""}立会希望日1`}
						</span>
						<span className={Styles.RequiredLabel}>
							<ItandiChip label="任意" />
						</span>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.DateTimeGroup}>
							<div className={Styles.DateInput}>
								<Datepicker
									onChange={(date): void => {
										onChangeDate(date, "inspectionCandidateDate1", setValue);
									}}
									selected={toDate(inspectionCandidateDate1)}
								/>
							</div>
							<div className={Styles.TimeInput}>
								<Input
									placeholder="例）午前中"
									{...register("inspectionCandidateDateTime1")}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>
							{`${isRestorationDealType ? "退去" : ""}立会希望日2`}
						</span>
						<span className={Styles.RequiredLabel}>
							<ItandiChip label="任意" />
						</span>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.DateTimeGroup}>
							<div className={Styles.DateInput}>
								<Datepicker
									onChange={(date): void => {
										onChangeDate(date, "inspectionCandidateDate2", setValue);
									}}
									selected={toDate(inspectionCandidateDate2)}
								/>
							</div>
							<div className={Styles.TimeInput}>
								<Input
									placeholder="例）12時-14時"
									{...register("inspectionCandidateDateTime2")}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>
							{`${isRestorationDealType ? "退去" : ""}立会希望日3`}
						</span>
						<span className={Styles.RequiredLabel}>
							<ItandiChip label="任意" />
						</span>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.DateTimeGroup}>
							<div className={Styles.DateInput}>
								<Datepicker
									onChange={(date): void => {
										onChangeDate(date, "inspectionCandidateDate3", setValue);
									}}
									selected={toDate(inspectionCandidateDate3)}
								/>
							</div>
							<div className={Styles.TimeInput}>
								<Input
									placeholder="例）午後"
									{...register("inspectionCandidateDateTime3")}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
